import { difference, filter, find, forEach, isEmpty, without } from "lodash";
import Big from 'big.js';
import { uuid } from "./utils";
import { MAX_PRODUCT_IMAGE_SIZE } from '../constants/AppConstants';

export const generateCombinations = (variants = [], combinations = [], removedCombinations = []) => {
  if (isEmpty(variants)) return [];

  const variantsWithOptions = variants.filter((el) => {
    return el._destroy !== true && !isEmpty(el.options.filter((opt) => opt._destroy !== true))
  });

  const options = variantsWithOptions.map(variant => variant.options.filter((el) => el._destroy !== true).map(option => ({
    name: option.name, web_id: option.web_id
  })));

  const allCombinations = combineVariants(options);
  const newCombinations = [];

  allCombinations.forEach(combination => {
    let combinationObj = {
      name: '',
      price: '0',
      quantity: '0',
      web_id: uuid(),
      variant_option_web_ids: [],
      url: null,
      selectedFile: null,
      track_quantity: true,
      selling_without_stock: false
    };

    combinationObj.name = combination.map(option => option.name).join('/');

    combination.forEach((option, index) => {
      combinationObj[variants[index].name] = option.name;
      combinationObj.variant_option_web_ids.push(option.web_id);
    });

    const existingCombination = getExistingCombination(combinationObj, combinations);

    if (!isEmpty(existingCombination)) {
      combinationObj = { ...existingCombination, name: combinationObj.name };
    };

    const combIn = find(removedCombinations, (comb) => (isSameArray(comb.variant_option_web_ids, combinationObj.variant_option_web_ids)));

    if (!combIn) newCombinations.push(combinationObj);
  });

  return newCombinations;
};

export const getExistingCombination = (combination = {}, combinations = []) => {
  if (isEmpty(combinations)) return {};

  let existingCombination = {};

  const web_ids_1 = combination.variant_option_web_ids
  let web_ids_2;

  forEach(combinations, (combination) => {
    web_ids_2 = combination.variant_option_web_ids;

    if (isSameCombination(web_ids_1, web_ids_2) && isSameCombination(web_ids_2, web_ids_1)) {
      existingCombination = combination;
    };
  });

  return existingCombination;
};

export const isSameCombination = (web_ids_1, web_ids_2) => {
  return isEmpty(difference(web_ids_1, web_ids_2));
};

export const combineVariants = (options) => {
  return combine(options);
};

export const groupCombinationsBy = (variants = [], combinations = [], selectedVariant = {}) => {
  if (isEmpty(variants) || isEmpty(selectedVariant) || isEmpty(combinations)) return [];

  const allOptions = variants.flatMap((variant) => variant.options);

  const formatNameCached = (web_ids = []) => {
    const webIdsSet = new Set(web_ids);
    const names = [];
    allOptions.forEach((option) => {
      if (webIdsSet.has(option.web_id)) {
        names.push(option.name);
      }
    });
    return names.join('/');
  };

  const newGroupedCombinations = (selectedVariant.new_options || [])
    .filter((el) => el._destroy !== true)
    .map((opt) => {
      const filteredOptions = combinations
        .filter((combination) => combination.variant_option_web_ids.includes(opt.web_id))
        .map((combination) => {
          const name = formatNameCached(combination.variant_option_web_ids);
          const newWebIds = combination.variant_option_web_ids.filter((id) => id !== opt.web_id);
          const short_name = formatNameCached(newWebIds);
          return {
            ...combination,
            name,
            short_name
          };
        });

      return {
        optionName: opt.name,
        combinations: filteredOptions,
        web_id: opt.web_id,
        price: '',
        quantity: ''
      };
    });

  return newGroupedCombinations.filter((group) => group.combinations.length > 0);
};

export const updateGroupedCombinations = (groups = [], combinations = []) => {
  if (isEmpty(groups)) return [];

  const newGroup = groups.map((group) => {
    const newCombinations = [];
    forEach(group.combinations, (combination) => {
      const combIn = find(combinations, { web_id: combination.web_id });
      if (combIn) newCombinations.push({ ...combIn });
    });

    return {
      ...group,
      combinations: newCombinations
    };
  });

  return filter(newGroup, (group) => (!isEmpty(group.combinations)));
};

export const removeCombinationFromGroup = (groupedCombinations = [], combination = {}) => {
  if (isEmpty(groupedCombinations)) return [];

  return groupedCombinations.map((groupedCombination) => {
    return {
      ...groupedCombination,
      combinations: filter(groupedCombination.combinations, (comb) => (comb.web_id != combination.web_id))
    };
  });
};

export const removeOptionAndVariantWithoutCombination = (variants = [], combinations = []) => {
  const newVariants = variants.map((variant) => {
    return {
      ...variant,
      options: variant.options.map((opt) => {
        return {
          ...opt,
          _destroy: isEmpty(filter(combinations, (comb) => (comb.variant_option_web_ids.includes(opt.web_id))))
        }
      })
    }
  });

  return filter(newVariants, (variant) => (!isEmpty(filter(variant.options, (opt) => (opt._destroy !== true)))));
};

export const isSameArray = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;

  return arr1.every((element, index) => element === arr2[index]);
};

export const validateAvailable = (item) => {
  if (item.wasSelected) return false;

  return !item.track_quantity || item.selling_without_stock || Number(item.quantity) > 0;
};

export const canBeSelected = (product = {}) => {
  if (!isEmpty(product.product_variant_combinations)) {
    const availables = filter(product.product_variant_combinations, (item) => validateAvailable(item));

    if (!isEmpty(availables)) return true;

    return false;
  };

  return validateAvailable(product);
};

export const isPurgeImageFlag = (value) => value === true || value === 'true';

export const removeCombinationFile = (combination, combinations) => {
  const hasServerImage = Boolean(
    combination.attached_image?.url ||
    (combination.image && !combination.selectedFile)
  );

  const newCombinations = combinations.map((comb) => {
    if (comb.web_id === combination.web_id) {
      revokeObjectUrl(comb.url);
      return {
        ...comb,
        selectedFile: null,
        url: '',
        attached_image: {},
        purge_image: hasServerImage ? 'true' : false
      };
    }

    return { ...comb };
  });

  return newCombinations;
};

export const isObjectUrl = (url) => typeof url === 'string' && url.startsWith('blob:');

export const revokeObjectUrl = (url) => {
  if (typeof URL === 'undefined' || !isObjectUrl(url)) return;
  URL.revokeObjectURL(url);
};

export const revokeCombinationPreviewUrl = (combination = {}) => {
  revokeObjectUrl(combination?.url);
};

export const buildCombinationImagePreview = (combination = {}, file) => {
  revokeCombinationPreviewUrl(combination);
  return {
    ...combination,
    selectedFile: file,
    purge_image: false,
    attached_image: {},
    url: URL.createObjectURL(file)
  };
};

export const revokePreviewUrlsFromCombinations = (combinations = []) => {
  combinations.forEach((combination) => revokeCombinationPreviewUrl(combination));
};

export const normalizeVariantsFromProduct = (savedProduct = {}) => (
  (savedProduct.product_variants || []).map((item) => ({
    id: item.id,
    name: item.name,
    web_id: item.web_id,
    _destroy: false,
    options: (item.product_variant_options || []).map((opt) => ({
      id: opt.id, name: opt.name, web_id: opt.web_id, _destroy: false
    }))
  }))
);

export const normalizeCombinationsFromProduct = (savedProduct = {}) => (
  (savedProduct.product_variant_combinations || []).map((combination) => ({
    ...combination,
    selectedFile: null,
    url: isEmpty(combination.attached_image) ? '' : combination.attached_image.url,
    _destroy: false
  }))
);

export const normalizeCombinationFromResponse = (combination = {}) => ({
  ...combination,
  selectedFile: null,
  url: isEmpty(combination.attached_image) ? '' : combination.attached_image.url,
  _destroy: false
});

export const reconcileSavedCombination = (combinations = [], savedCombination = {}) => {
  const normalizedCombination = normalizeCombinationFromResponse(savedCombination);
  let wasUpdated = false;

  const nextCombinations = combinations.map((combination) => {
    if (combination.web_id !== normalizedCombination.web_id) {
      return { ...combination };
    }

    wasUpdated = true;
    revokeCombinationPreviewUrl(combination);
    return {
      ...combination,
      ...normalizedCombination
    };
  });

  if (!wasUpdated) nextCombinations.push(normalizedCombination);

  return nextCombinations;
};

function formatName(allOptions, web_ids = []) {
  const names = allOptions
    .filter(option => web_ids.includes(option.web_id))
    .map(option => option.name);

  return names.join('/');
};

function combine(options) {
  if (options.length === 0) return [[]];
  const firstOption = options[0];
  const restOptions = options.slice(1);
  const combinationsWithoutFirst = combine(restOptions);
  const allCombinations = [];

  firstOption.forEach(option => {
    combinationsWithoutFirst.forEach(combination => {
      allCombinations.push([option].concat(combination));
    });
  });

  return allCombinations;
};

export const isValidImageSize = (file) => { return file.size <= MAX_PRODUCT_IMAGE_SIZE };

export const getProductTaxAmount = (product, retailerTaxAmount = 0) => {
  if (!product) return Number(retailerTaxAmount) || 0;

  const { type_tax: typeTax } = product;

  if (typeTax === 'tax_free' || typeTax === 1) return 0;

  if (typeTax === 'custom_tax' || typeTax === 0) {
    const productCustomTax = product.custom_tax;
    if (productCustomTax != null && productCustomTax !== '') {
      return Number(productCustomTax) || 0;
    }
  }

  return Number(retailerTaxAmount) || 0;
};

export const hasDiscount = (product = {}) => {
  if (!product) return false;
  const discountedPrice = Number(product.discounted_price);
  const price = Number(product.price);
  return Boolean(discountedPrice > 0 && price > 0 && discountedPrice < price);
};

